import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { WEEKDAYS, isoWeek, parseISODate, todayISO } from '../utils/dates';
import './SalesFormPage.css';

const BLANK = {
  farm: '', transportation: '', boxes: '', pallets: '', fruit_weight: '',
  company: '', exporter: '', brand: '', box_type: '', comment: '',
};

export default function SalesFormPage() {
  const { id: saleId } = useParams();
  const isEdit = Boolean(saleId);
  const navigate = useNavigate();
  const [original, setOriginal] = useState(null);
  const [farms, setFarms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [transportations, setTransportations] = useState([]);
  const [exporters, setExporters] = useState([]);
  const [brands, setBrands] = useState([]);
  const [boxTypes, setBoxTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...BLANK, date: todayISO() });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const selectedDate = form.date ? parseISODate(form.date) : null;
  const dayLabel = selectedDate ? WEEKDAYS[selectedDate.getDay()] : '';
  const weekLabel = selectedDate ? isoWeek(selectedDate) : '';

  useEffect(() => {
    Promise.all([
      api.get('farms/'), api.get('companies/'), api.get('transportations/'),
      api.get('exporters/'), api.get('brands/'), api.get('box-types/'),
      isEdit ? api.get(`sales/${saleId}/`) : Promise.resolve(null),
    ])
      .then(([f, c, t, e, b, bt, s]) => {
        setFarms(f.data);
        setCompanies(c.data);
        setTransportations(t.data);
        setExporters(e.data);
        setBrands(b.data);
        setBoxTypes(bt.data);
        if (s) {
          const sale = s.data;
          setOriginal(sale);
          setForm({
            date: sale.date,
            company: String(sale.company),
            farm: String(sale.farm),
            exporter: String(sale.exporter),
            brand: String(sale.brand),
            box_type: sale.box_type ? String(sale.box_type) : '',
            transportation: String(sale.transportation),
            boxes: String(sale.boxes),
            pallets: String(sale.pallets),
            fruit_weight: String(Number(sale.fruit_weight)).replace('.', ','),
            comment: sale.comment || '',
          });
        } else if (c.data.length === 1) {
          // A user restricted to one company only ever gets that one back — preselect it.
          setForm((fm) => ({ ...fm, company: String(c.data[0].id) }));
        }
      })
      .catch(() => setStatus({ type: 'error', text: isEdit ? 'No se pudo cargar la venta.' : 'No se pudieron cargar las listas.' }))
      .finally(() => setLoading(false));
  }, [isEdit, saleId]);

  const visibleFarms = useMemo(
    () => (form.company ? farms.filter((f) => String(f.company) === form.company) : farms),
    [farms, form.company],
  );
  const selectedFarm = farms.find((f) => String(f.id) === form.farm);
  // An edited sale keeps the MAGAP it was saved with unless its hacienda changes.
  const magapShown = original && String(original.farm) === form.farm
    ? original.magap
    : selectedFarm?.magap;

  const set = (field) => (e) => setForm((fm) => ({ ...fm, [field]: e.target.value }));

  const handleFarmChange = (e) => {
    const farmId = e.target.value;
    const farm = farms.find((f) => String(f.id) === farmId);
    setForm((fm) => ({ ...fm, farm: farmId, company: farm ? String(farm.company) : fm.company }));
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setForm((fm) => {
      const farm = farms.find((f) => String(f.id) === fm.farm);
      const keepFarm = farm && String(farm.company) === companyId;
      return { ...fm, company: companyId, farm: keepFarm ? fm.farm : '' };
    });
  };

  const onlyDigits = (field) => (e) => {
    const v = e.target.value.replace(/\D/g, '');
    setForm((fm) => ({ ...fm, [field]: v }));
  };

  const onDecimal = (field) => (e) => {
    const v = e.target.value.replace(/[^\d.,]/g, '');
    setForm((fm) => ({ ...fm, [field]: v }));
  };

  const weightNumber = Number(form.fruit_weight.replace(',', '.'));
  const isComplete = form.date && form.farm && form.transportation && form.boxes && form.pallets
    && form.fruit_weight && !Number.isNaN(weightNumber)
    && form.company && form.exporter && form.brand && form.box_type;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete) return;
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        date: form.date,
        farm: Number(form.farm),
        transportation: Number(form.transportation),
        boxes: Number(form.boxes),
        pallets: Number(form.pallets),
        fruit_weight: weightNumber.toFixed(2),
        company: Number(form.company),
        exporter: Number(form.exporter),
        brand: Number(form.brand),
        box_type: Number(form.box_type),
        comment: form.comment.trim(),
      };
      if (isEdit) {
        await api.patch(`sales/${saleId}/`, payload);
        navigate('/ventas/historial');
        return;
      }
      await api.post('sales/', payload);
      setStatus({ type: 'success', text: 'Venta registrada correctamente.' });
      // Keep the date and company: several sheets are usually entered for the same day.
      setForm((fm) => ({ ...BLANK, date: fm.date, company: companies.length === 1 ? fm.company : '' }));
    } catch (err) {
      const detail = err.response?.data;
      const msg = detail?.farm?.[0] || detail?.detail || 'No se pudo guardar la venta.';
      setStatus({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const listSelect = (value, onChange, options, emptyLabel, labelOf = (o) => o.name) => (
    <select className="sales-cell-input" value={value} onChange={onChange}>
      <option value="">{emptyLabel}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{labelOf(o)}</option>)}
    </select>
  );

  return (
    <div className="sales-form-root">
      <div className="sales-form-topbar">
        <Link to="/ventas" className="sales-back-link">← Ventas</Link>
        <Link to="/ventas/historial" className="sales-back-link">Historial de Ventas →</Link>
      </div>

      {status && <div className={`sales-status sales-status-${status.type}`}>{status.text}</div>}

      {loading ? (
        <div className="sales-empty">Cargando…</div>
      ) : (
        <form onSubmit={handleSubmit}>
          <table className="sales-sheet">
            <thead>
              <tr><th colSpan={2} className="sales-sheet-header">COSECHA</th></tr>
            </thead>
            <tbody>
              <tr>
                <td className="sales-label">FECHA</td>
                <td className="sales-value">
                  <input type="date" className="sales-cell-input" value={form.date} onChange={set('date')} required />
                </td>
              </tr>
              <tr>
                <td className="sales-label">SEMANA</td>
                <td className="sales-value">{weekLabel}</td>
              </tr>
              <tr>
                <td className="sales-label">DIA</td>
                <td className="sales-value">{dayLabel}</td>
              </tr>
              <tr>
                <td className="sales-label">EMPRESA</td>
                <td className="sales-value">{listSelect(form.company, handleCompanyChange, companies, '— seleccionar —')}</td>
              </tr>
              <tr>
                <td className="sales-label">HACIENDA</td>
                <td className="sales-value">{listSelect(form.farm, handleFarmChange, visibleFarms, '— seleccionar —')}</td>
              </tr>
              <tr>
                <td className="sales-label">CODIGO MAGAP</td>
                <td className="sales-value">
                  {selectedFarm ? (magapShown || <span className="sales-muted">sin código</span>) : ''}
                </td>
              </tr>
              <tr>
                <td className="sales-label">EXPORTADORA</td>
                <td className="sales-value">{listSelect(form.exporter, set('exporter'), exporters, '— seleccionar —')}</td>
              </tr>
              <tr>
                <td className="sales-label">MARCA DE LA CAJA</td>
                <td className="sales-value">{listSelect(form.brand, set('brand'), brands, '— seleccionar —')}</td>
              </tr>
              <tr>
                <td className="sales-label">TIPO DE CAJA</td>
                <td className="sales-value">
                  {listSelect(form.box_type, set('box_type'), boxTypes, '— seleccionar —', (o) => o.type)}
                </td>
              </tr>
              <tr>
                <td className="sales-label">TRANSPORTE</td>
                <td className="sales-value">{listSelect(form.transportation, set('transportation'), transportations, '— seleccionar —')}</td>
              </tr>
              <tr>
                <td className="sales-label">CANTIDAD DE CAJAS</td>
                <td className="sales-value sales-manual">
                  <input className="sales-cell-input" inputMode="numeric" value={form.boxes} onChange={onlyDigits('boxes')} />
                </td>
              </tr>
              <tr>
                <td className="sales-label">PALLETS</td>
                <td className="sales-value sales-manual">
                  <input className="sales-cell-input" inputMode="numeric" value={form.pallets} onChange={onlyDigits('pallets')} />
                </td>
              </tr>
              <tr>
                <td className="sales-label">PESO CAJA FRUTA</td>
                <td className="sales-value sales-manual">
                  <input className="sales-cell-input" inputMode="decimal" value={form.fruit_weight} onChange={onDecimal('fruit_weight')} />
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="sales-observation">
                  <label className="sales-observation-inner">
                    <span className="sales-observation-label">OBSERVACIÓN:</span>
                    <input
                      className="sales-cell-input sales-observation-input"
                      value={form.comment}
                      onChange={(e) => setForm((fm) => ({ ...fm, comment: e.target.value.toUpperCase() }))}
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="sales-actions">
            {isEdit && (
              <Link to="/ventas/historial" className="sales-cancel-btn">Cancelar</Link>
            )}
            <button className="sales-save-btn" type="submit" disabled={saving || !isComplete}>
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Guardar venta'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
